import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ActionIcon,
  Badge,
  Button,
  Group,
  Loader,
  Paper,
  Stack,
  Table,
  Tabs,
  Text,
  Tooltip,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import {
  IconEdit,
  IconPercentage,
  IconPlus,
  IconStack2,
  IconTrash,
} from '@tabler/icons-react';
import { PageHeader } from '../../components/PageHeader';
import { EmptyState } from '../../components/EmptyState';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import {
  useDeleteTax,
  useDeleteTaxGroup,
  useTaxes,
  useTaxGroups,
} from './taxesApi';
import { TaxFormDrawer } from './TaxFormDrawer';
import { TaxGroupFormDrawer } from './TaxGroupFormDrawer';
import type { TaxDto, TaxGroupDto } from './types';

export function TaxesPage() {
  const { t, i18n } = useTranslation();
  const { data: taxes, isLoading: taxesLoading } = useTaxes();
  const { data: groups, isLoading: groupsLoading } = useTaxGroups();
  const deleteTax = useDeleteTax();
  const deleteGroup = useDeleteTaxGroup();

  const [tab, setTab] = useState<string | null>('taxes');

  const [taxDrawerOpen, setTaxDrawerOpen] = useState(false);
  const [editingTax, setEditingTax] = useState<TaxDto | null>(null);
  const [deletingTax, setDeletingTax] = useState<TaxDto | null>(null);

  const [groupDrawerOpen, setGroupDrawerOpen] = useState(false);
  const [editingGroup, setEditingGroup] = useState<TaxGroupDto | null>(null);
  const [deletingGroup, setDeletingGroup] = useState<TaxGroupDto | null>(null);

  // taxId → display label for the tax-group "Includes" column
  const taxLabelById = useMemo(() => {
    const map = new Map<string, string>();
    (taxes ?? []).forEach((tax) =>
      map.set(
        tax.taxId,
        i18n.language === 'ar' ? tax.nameAr : (tax.nameEn ?? tax.nameAr),
      ),
    );
    return map;
  }, [taxes, i18n.language]);

  const openCreateTax = () => {
    setEditingTax(null);
    setTaxDrawerOpen(true);
  };
  const openEditTax = (tax: TaxDto) => {
    setEditingTax(tax);
    setTaxDrawerOpen(true);
  };
  const openCreateGroup = () => {
    setEditingGroup(null);
    setGroupDrawerOpen(true);
  };
  const openEditGroup = (group: TaxGroupDto) => {
    setEditingGroup(group);
    setGroupDrawerOpen(true);
  };

  const confirmDeleteTax = async () => {
    if (!deletingTax) return;
    try {
      await deleteTax.mutateAsync(deletingTax.taxId);
      notifications.show({
        color: 'green',
        title: t('common.toasts.deleted'),
        message: deletingTax.nameAr,
      });
      setDeletingTax(null);
    } catch (err) {
      notifications.show({
        color: 'red',
        title: t('common.toasts.errorTitle'),
        message: apiMessage(err) ?? t('common.toasts.errorBody'),
      });
    }
  };

  const confirmDeleteGroup = async () => {
    if (!deletingGroup) return;
    try {
      await deleteGroup.mutateAsync(deletingGroup.taxGroupId);
      notifications.show({
        color: 'green',
        title: t('common.toasts.deleted'),
        message: deletingGroup.nameAr,
      });
      setDeletingGroup(null);
    } catch (err) {
      notifications.show({
        color: 'red',
        title: t('common.toasts.errorTitle'),
        message: apiMessage(err) ?? t('common.toasts.errorBody'),
      });
    }
  };

  const hasTaxes = (taxes?.length ?? 0) > 0;
  const hasGroups = (groups?.length ?? 0) > 0;

  return (
    <Stack gap="lg">
      <PageHeader
        eyebrow={t('nav.sections.catalog')}
        title={t('taxes.page.title')}
        subtitle={t('taxes.page.subtitle')}
        actions={
          tab === 'groups' ? (
            <Button leftSection={<IconPlus size={16} stroke={1.8} />} onClick={openCreateGroup}>
              {t('taxGroups.page.create')}
            </Button>
          ) : (
            <Button leftSection={<IconPlus size={16} stroke={1.8} />} onClick={openCreateTax}>
              {t('taxes.page.create')}
            </Button>
          )
        }
      />

      <Tabs value={tab} onChange={setTab} variant="default">
        <Tabs.List>
          <Tabs.Tab value="taxes" leftSection={<IconPercentage size={16} stroke={1.6} />}>
            {t('taxes.tabs.taxes')}
          </Tabs.Tab>
          <Tabs.Tab value="groups" leftSection={<IconStack2 size={16} stroke={1.6} />}>
            {t('taxes.tabs.groups')}
          </Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="taxes" pt="md">
          <Paper p={hasTaxes ? 0 : 'lg'}>
            {taxesLoading ? (
              <Stack align="center" py={48}>
                <Loader size="sm" />
              </Stack>
            ) : !hasTaxes ? (
              <EmptyState
                icon={<IconPercentage size={22} stroke={1.6} />}
                title={t('taxes.empty.title')}
                description={t('taxes.empty.body')}
                actionLabel={t('taxes.page.create')}
                onAction={openCreateTax}
              />
            ) : (
              <Table verticalSpacing="md" highlightOnHover striped="even">
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>{t('taxes.columns.code')}</Table.Th>
                    <Table.Th>{t('taxes.columns.name')}</Table.Th>
                    <Table.Th>{t('taxes.columns.rate')}</Table.Th>
                    <Table.Th>{t('taxes.columns.kind')}</Table.Th>
                    <Table.Th style={{ width: 100, textAlign: 'end' }}>
                      {t('common.actions')}
                    </Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {taxes!.map((tax) => (
                    <Table.Tr key={tax.taxId}>
                      <Table.Td>
                        <span className="font-numeric font-mono text-xs text-[var(--wheels-ink-muted)]">
                          {tax.code}
                        </span>
                      </Table.Td>
                      <Table.Td>
                        <Text fw={500} c="var(--wheels-ink-strong)">
                          {tax.nameAr}
                        </Text>
                        {tax.nameEn && (
                          <Text fz="xs" c="var(--wheels-ink-muted)">
                            {tax.nameEn}
                          </Text>
                        )}
                      </Table.Td>
                      <Table.Td>
                        <Text fw={500} className="font-numeric">
                          {tax.rate}%
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        {tax.isInclusive ? (
                          <Badge color="blue" variant="light" radius="sm">
                            {t('taxes.kind.inclusive')}
                          </Badge>
                        ) : (
                          <Badge color="gray" variant="light" radius="sm">
                            {t('taxes.kind.exclusive')}
                          </Badge>
                        )}
                      </Table.Td>
                      <Table.Td>
                        <Group gap="xs" justify="flex-end" wrap="nowrap">
                          <Tooltip label={t('common.edit')} withinPortal>
                            <ActionIcon
                              variant="subtle"
                              color="gray"
                              onClick={() => openEditTax(tax)}
                              aria-label={t('common.edit')}
                            >
                              <IconEdit size={16} stroke={1.6} />
                            </ActionIcon>
                          </Tooltip>
                          <Tooltip label={t('common.delete')} withinPortal>
                            <ActionIcon
                              variant="subtle"
                              color="red"
                              onClick={() => setDeletingTax(tax)}
                              aria-label={t('common.delete')}
                            >
                              <IconTrash size={16} stroke={1.6} />
                            </ActionIcon>
                          </Tooltip>
                        </Group>
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            )}
          </Paper>
        </Tabs.Panel>

        <Tabs.Panel value="groups" pt="md">
          <Paper p={hasGroups ? 0 : 'lg'}>
            {groupsLoading ? (
              <Stack align="center" py={48}>
                <Loader size="sm" />
              </Stack>
            ) : !hasGroups ? (
              <EmptyState
                icon={<IconStack2 size={22} stroke={1.6} />}
                title={t('taxGroups.empty.title')}
                description={t('taxGroups.empty.body')}
                actionLabel={t('taxGroups.page.create')}
                onAction={openCreateGroup}
              />
            ) : (
              <Table verticalSpacing="md" highlightOnHover striped="even">
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>{t('taxGroups.columns.code')}</Table.Th>
                    <Table.Th>{t('taxGroups.columns.name')}</Table.Th>
                    <Table.Th>{t('taxGroups.columns.includes')}</Table.Th>
                    <Table.Th style={{ width: 100, textAlign: 'end' }}>
                      {t('common.actions')}
                    </Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {groups!.map((group) => (
                    <Table.Tr key={group.taxGroupId}>
                      <Table.Td>
                        <span className="font-numeric font-mono text-xs text-[var(--wheels-ink-muted)]">
                          {group.code}
                        </span>
                      </Table.Td>
                      <Table.Td>
                        <Text fw={500} c="var(--wheels-ink-strong)">
                          {group.nameAr}
                        </Text>
                        {group.nameEn && (
                          <Text fz="xs" c="var(--wheels-ink-muted)">
                            {group.nameEn}
                          </Text>
                        )}
                      </Table.Td>
                      <Table.Td>
                        {group.taxIds.length === 0 ? (
                          <Text fz="sm" c="var(--wheels-ink-muted)">
                            —
                          </Text>
                        ) : (
                          <Group gap={4}>
                            {group.taxIds.map((id) => (
                              <Badge key={id} variant="light" color="gray" radius="sm">
                                {taxLabelById.get(id) ?? id}
                              </Badge>
                            ))}
                          </Group>
                        )}
                      </Table.Td>
                      <Table.Td>
                        <Group gap="xs" justify="flex-end" wrap="nowrap">
                          <Tooltip label={t('common.edit')} withinPortal>
                            <ActionIcon
                              variant="subtle"
                              color="gray"
                              onClick={() => openEditGroup(group)}
                              aria-label={t('common.edit')}
                            >
                              <IconEdit size={16} stroke={1.6} />
                            </ActionIcon>
                          </Tooltip>
                          <Tooltip label={t('common.delete')} withinPortal>
                            <ActionIcon
                              variant="subtle"
                              color="red"
                              onClick={() => setDeletingGroup(group)}
                              aria-label={t('common.delete')}
                            >
                              <IconTrash size={16} stroke={1.6} />
                            </ActionIcon>
                          </Tooltip>
                        </Group>
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            )}
          </Paper>
        </Tabs.Panel>
      </Tabs>

      <TaxFormDrawer
        opened={taxDrawerOpen}
        onClose={() => setTaxDrawerOpen(false)}
        tax={editingTax}
      />
      <TaxGroupFormDrawer
        opened={groupDrawerOpen}
        onClose={() => setGroupDrawerOpen(false)}
        group={editingGroup}
      />

      <ConfirmDialog
        opened={deletingTax !== null}
        onClose={() => setDeletingTax(null)}
        onConfirm={confirmDeleteTax}
        title={t('taxes.delete.title')}
        message={t('taxes.delete.body', { name: deletingTax?.nameAr ?? '' })}
        loading={deleteTax.isPending}
      />
      <ConfirmDialog
        opened={deletingGroup !== null}
        onClose={() => setDeletingGroup(null)}
        onConfirm={confirmDeleteGroup}
        title={t('taxGroups.delete.title')}
        message={t('taxGroups.delete.body', { name: deletingGroup?.nameAr ?? '' })}
        loading={deleteGroup.isPending}
      />
    </Stack>
  );
}

function apiMessage(err: unknown): string | null {
  if (typeof err === 'object' && err !== null && 'response' in err) {
    const data = (err as { response?: { data?: { detail?: string; title?: string } } })
      .response?.data;
    return data?.detail ?? data?.title ?? null;
  }
  return null;
}
